import { Component, type FC, type ReactNode } from 'react';

interface ErrorBoundaryProps {
	fallback?: FC<{ error?: Error } | Record<string, unknown>>;
	children?: ReactNode;
}

/**
 * Minimal generic error boundary. PR 3b removed Zod's per-node tolerant
 * skip, so a malformed node with a known type but invalid payload can
 * throw during render. Wrapping each node renderer keeps one bad node
 * from tearing down the whole message view.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, { hasError: boolean; error?: Error }> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: undefined };
	}

	componentDidCatch(error: Error) {
		console.error('ErrorBoundary caught an error:', error);
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback ? <this.props.fallback error={this.state.error} /> : null;
		}

		return this.props.children;
	}
}
